import { Fragment, useState, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  LightBulbIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { apiHelpers } from '../../services/api';
import { useCreditCosts } from '../../hooks/useCreditCosts';
import toast from 'react-hot-toast';

const SCHEDULE_OPTIONS = [
  { value: 'manual', label: 'Manual refresh' },
  { value: 'every_12_hours', label: 'Every 12 hours' },
  { value: 'daily', label: 'Once a day' },
  { value: 'weekly', label: 'Once a week' },
  { value: 'monthly', label: 'Once a month' }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

export default function AddInsightBlockModal({ isOpen, onClose, dashboardId, onInsightCreated }) {
  const { formatCost } = useCreditCosts();

  const [prompt, setPrompt] = useState('');
  const [scheduleType, setScheduleType] = useState('manual');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);

  const [isGenerating, setIsGenerating] = useState(false);
  const [progressText, setProgressText] = useState('');

  const resetState = () => {
    setPrompt('');
    setScheduleType('manual');
    setScheduleTime('09:00');
    setDayOfWeek(1);
    setDayOfMonth(1);
    setIsGenerating(false);
    setProgressText('');
  };

  const handleClose = () => {
    if (isGenerating) return;
    resetState();
    onClose();
  };

  const buildRefreshSchedule = () => {
    const schedule = { type: scheduleType };
    if (['daily', 'weekly', 'monthly', 'every_12_hours'].includes(scheduleType)) {
      schedule.time = scheduleTime;
    }
    if (scheduleType === 'weekly') {
      schedule.dayOfWeek = dayOfWeek;
    }
    if (scheduleType === 'monthly') {
      schedule.dayOfMonth = dayOfMonth;
    }
    return schedule;
  };

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setIsGenerating(true);
    setProgressText('Starting...');

    try {
      const result = await apiHelpers.createInsightBlock(
        {
          prompt: prompt.trim(),
          dashboardId,
          refreshSchedule: buildRefreshSchedule()
        },
        (progress) => {
          setProgressText(progress.text || 'Processing...');
          if (progress.details) {
            setProgressText(prev => `${progress.text} ${progress.details}`);
          }
        }
      );

      const insightBlock = result.data?.insightBlock;
      if (insightBlock) {
        if (onInsightCreated) {
          onInsightCreated(insightBlock, result.data?.layoutItem);
        }
        toast.success('Insight block created!');
        resetState();
        onClose();
      }
    } catch (error) {
      console.error('Create insight block error:', error);
      toast.error(error.message || 'Failed to create insight block');
      setIsGenerating(false);
    }
  }, [prompt, dashboardId, onInsightCreated, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform bg-white dark:bg-surface-900 rounded-2xl shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                      <LightBulbIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-surface-900 dark:text-white">
                        Add Insight Block
                      </Dialog.Title>
                      <p className="text-sm text-surface-500">AI-generated analysis on your dashboard</p>
                    </div>
                  </div>
                  <button onClick={handleClose} disabled={isGenerating} className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 disabled:opacity-50">
                    <XMarkIcon className="w-5 h-5 text-surface-500" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
                  {/* Prompt */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      What would you like to know?
                    </label>
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      disabled={isGenerating}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white placeholder-surface-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none disabled:opacity-50"
                      placeholder={'e.g. "Write a 3-bullet summary comparing this month\u2019s procedures to last month\u2019s, highlighting anomalies."'}
                    />
                  </div>

                  {/* Refresh Schedule */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      Refresh Schedule
                    </label>
                    <div className="flex flex-wrap gap-3">
                      <select
                        value={scheduleType}
                        onChange={(e) => setScheduleType(e.target.value)}
                        disabled={isGenerating}
                        className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white text-sm disabled:opacity-50"
                      >
                        {SCHEDULE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>

                      {['daily', 'weekly', 'monthly', 'every_12_hours'].includes(scheduleType) && (
                        <input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          disabled={isGenerating}
                          className="px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white text-sm disabled:opacity-50"
                        />
                      )}

                      {scheduleType === 'weekly' && (
                        <select
                          value={dayOfWeek}
                          onChange={(e) => setDayOfWeek(Number(e.target.value))}
                          disabled={isGenerating}
                          className="px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white text-sm disabled:opacity-50"
                        >
                          {DAYS_OF_WEEK.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </select>
                      )}

                      {scheduleType === 'monthly' && (
                        <select
                          value={dayOfMonth}
                          onChange={(e) => setDayOfMonth(Number(e.target.value))}
                          disabled={isGenerating}
                          className="px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white text-sm disabled:opacity-50"
                        >
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                            <option key={d} value={d}>Day {d}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-surface-400">
                      {scheduleType === 'manual'
                        ? `You can manually refresh anytime (1-hour cooldown). Each refresh costs ${formatCost('insight_block_refresh', { showParens: false }) || '5 credits'}.`
                        : `Auto-refreshes run on schedule and cost ${formatCost('insight_block_refresh', { showParens: false }) || '5 credits'} each. Manual refreshes also cost the same.`}
                    </p>
                  </div>

                  {/* Progress */}
                  {isGenerating && (
                    <div className="flex items-center gap-3 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      <span className="text-sm text-primary-700 dark:text-primary-300">{progressText}</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-surface-200 dark:border-surface-700">
                  <span className="text-xs text-surface-400">
                    {formatCost('insight_block_create') && `Cost: ${formatCost('insight_block_create', { showParens: false })}`}
                  </span>
                  <div className="flex gap-3">
                    <button
                      onClick={handleClose}
                      disabled={isGenerating}
                      className="px-4 py-2 text-sm font-medium text-surface-700 dark:text-surface-300 bg-surface-100 dark:bg-surface-800 rounded-lg hover:bg-surface-200 dark:hover:bg-surface-700 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={isGenerating || !prompt.trim()}
                      className="btn-primary disabled:opacity-50"
                    >
                      {isGenerating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="w-4 h-4 mr-2" />
                          Generate Insight
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
